import { useState, useEffect, useRef, useCallback } from "react";
import { Ticket, Truck, Check, X, Loader2 } from "lucide-react";
import { getCartQuote } from "../lib/api";
import FreeDeliveryBanner from "./FreeDeliveryBanner";
import { chf } from "../lib/format";
import { computeConfiguredPromo } from "../store/cartStore";

const GREEN = "#16a34a";

// Composant autonome : champ code promo + récap des formules La BASE (livraison offerte, 2e à -50%, code promo).
// Le calcul est AUTORITAIRE côté serveur (/api/cart/quote). Rend le récap et remonte le devis via onQuote.
export default function CheckoutFormules({ items, onQuote }) {
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(null);     // code effectivement appliqué
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onQuoteRef = useRef(onQuote);
  onQuoteRef.current = onQuote;
  const appliedRef = useRef(null);
  const payload = items.map((i) => ({ id: i.id, price: i.price, quantity: i.quantity }));
  const payloadKey = JSON.stringify(payload);

  const fetchQuote = useCallback(async (withCode) => {
    setBusy(true); setError("");
    try {
      const q = await getCartQuote(JSON.parse(payloadKey), withCode || null);
      setQuote(q);
      appliedRef.current = withCode || null;
      setApplied(withCode || null);
      onQuoteRef.current?.({ ...q, promo_code: withCode || null });
      return q;
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Code promo invalide ou expiré");
      return null;
    } finally { setBusy(false); }
  }, [payloadKey]);

  // Recalcule le devis quand le panier change (en conservant le code appliqué).
  useEffect(() => { fetchQuote(appliedRef.current); }, [fetchQuote]);

  const apply = async () => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    const q = await fetchQuote(c);
    if (q) setCode("");
  };
  const removeCode = async () => { setCode(""); await fetchQuote(null); };

  const secondHalf = quote?.second_half_discount || 0;
  const codeDiscount = quote?.code_discount || 0;
  const freeDelivery = !!quote?.free_delivery;
  const cfgPromo = computeConfiguredPromo(items);
  const totalProducts = quote ? Math.max(0, (quote.total_products || 0) - cfgPromo.discount) : 0;

  return (
    <div data-testid="checkout-formules">
      {quote && <FreeDeliveryBanner subtotal={quote.subtotal} freeDelivery={freeDelivery} />}
      {/* Champ code promo */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center gap-2 px-4 rounded-xl py-3 flex-1 bg-white" style={{ border: "1px solid #e5e7eb" }}>
          <Ticket size={20} color="#0b1220" />
          <input
            data-testid="checkout-promo-input"
            className="flex-1 bg-transparent outline-none text-[15px] uppercase"
            placeholder="Code promo"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
            disabled={busy}
          />
        </div>
        <button
          type="button"
          data-testid="checkout-promo-apply"
          onClick={apply}
          disabled={busy || !code.trim()}
          className="rounded-xl font-bold text-white px-5 py-3 flex items-center gap-1.5"
          style={{ background: "#0b1220", opacity: busy || !code.trim() ? 0.5 : 1 }}
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : "Appliquer"}
        </button>
      </div>

      {error && <p className="text-xs mb-3 font-semibold" style={{ color: "#dc2626" }} data-testid="checkout-promo-error">{error}</p>}

      {applied && (
        <div className="flex items-center justify-between rounded-xl px-4 py-2.5 mb-3" style={{ background: "#ecfdf5", border: "1px solid #a7f3d0" }} data-testid="checkout-promo-applied">
          <span className="flex items-center gap-2 text-sm font-bold" style={{ color: "#047857" }}><Check size={16} /> Code {applied} appliqué</span>
          <button type="button" data-testid="checkout-promo-remove" onClick={removeCode} style={{ color: "#047857" }}><X size={16} /></button>
        </div>
      )}

      {/* Récap */}
      {quote && (
        <div className="bg-white rounded-2xl p-5" style={{ border: "1px solid #f0f0f0" }} data-testid="checkout-breakdown">
          <div className="flex justify-between text-sm mb-2" style={{ color: "#475569" }}>
            <span>Sous-total</span><span className="font-semibold" data-testid="checkout-subtotal">{chf(quote.subtotal)}</span>
          </div>
          {secondHalf > 0 && (
            <div className="flex justify-between text-sm mb-2" style={{ color: GREEN }} data-testid="checkout-secondhalf">
              <span>2e article à −50 %</span><span className="font-semibold">−{chf(secondHalf)}</span>
            </div>
          )}
          {cfgPromo.lines.map((l) => (
            <div key={l.id} className="flex justify-between text-sm mb-2" style={{ color: GREEN }} data-testid={`checkout-cfgpromo-${l.id}`}>
              <span>{l.title} · {l.label}</span><span className="font-semibold">−{chf(l.amount)}</span>
            </div>
          ))}
          {codeDiscount > 0 && (
            <div className="flex justify-between text-sm mb-2" style={{ color: GREEN }} data-testid="checkout-codediscount">
              <span>Code {applied}</span><span className="font-semibold">−{chf(codeDiscount)}</span>
            </div>
          )}
          {freeDelivery && (
            <div className="flex justify-between text-sm mb-2 font-semibold" style={{ color: GREEN }} data-testid="checkout-freedelivery">
              <span className="flex items-center gap-1.5"><Truck size={15} /> Livraison offerte</span><span>Incluse</span>
            </div>
          )}
          <div className="flex justify-between items-center mt-3 pt-3" style={{ borderTop: "1px solid #e5e7eb" }}>
            <span className="font-extrabold" style={{ color: "#0b1220" }}>Total produits</span>
            <span className="text-xl font-extrabold" style={{ color: "#0b1220" }} data-testid="checkout-total">{chf(totalProducts)}</span>
          </div>
          <p className="text-xs mt-2" style={{ color: "#94a3b8" }}>
            {freeDelivery ? "Livraison offerte sur cette commande." : "Les frais de livraison sont calculés selon votre adresse de livraison."}
          </p>
        </div>
      )}
    </div>
  );
}
